import { useState } from 'react';
import { useBlogs, useDeleteBlog, useTogglePublish } from '@/lib/api/hooks/use-blogs';
import { PageHeader } from '@/components/layout/page-header';
import { Plus, FileText, Grid3X3, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BlogCard } from './components/blog-card';
import { BlogListItem } from './components/blog-list-item';
import { AuthorManagement } from './components/author-management';
import { GridContainer } from '@/components/layout/grid-container';
import { EmptyState } from '@/components/ui/empty-state';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { Blog } from '@/lib/api/types';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/lib/hooks/use-user-role';
import { motion } from 'framer-motion';

export default function BlogsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { data: userRole, isLoading: isRoleLoading } = useUserRole();

  const filters = {
    search: search || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  };

  const { data: blogs = [], isLoading } = useBlogs(filters);
  const deleteBlog = useDeleteBlog();
  const togglePublish = useTogglePublish();
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (!selectedBlog) return;
    await deleteBlog.mutateAsync(selectedBlog.id);
    setIsDeleteDialogOpen(false);
    setSelectedBlog(null);
  };

  const handleTogglePublish = async (blogId: string, currentStatus: string) => {
    await togglePublish.mutateAsync(blogId);
  };

  if (isLoading || isRoleLoading) {
    return <div className="text-foreground">Loading...</div>;
  }

  const isAuthor = userRole?.isAuthor || false;

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader
        title="Blogs"
        action={{
          label: 'Create Blog',
          icon: Plus,
          onClick: () => navigate('/blogs/new'),
        }}
      />

      <Tabs defaultValue="posts" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="posts">Posts</TabsTrigger>
          {!isAuthor && <TabsTrigger value="authors">Authors</TabsTrigger>}
        </TabsList>
        
        <TabsContent value="posts" className="space-y-6">
          {/* Filters and View Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between"
          >
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Input
                placeholder={isAuthor ? "Search your blogs..." : "Search blogs..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:max-w-xs"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full sm:w-auto px-4 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            {/* View Toggle */}
            <div className="flex border border-border rounded-md overflow-hidden w-full sm:w-auto">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-lg border-0 flex-1 sm:flex-initial"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-lg border-0 flex-1 sm:flex-initial"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>

          {/* Blog Content */}
          {blogs.length === 0 ? (
            <EmptyState
              icon={FileText}
              title={isAuthor ? "No blogs yet" : "No blogs yet"}
              description={isAuthor 
                ? "Start writing your first blog post to share with your audience."
                : "No blog posts have been created yet."
              }
              action={{
                label: 'Create Blog',
                onClick: () => navigate('/blogs/new'),
              }}
            />
          ) : viewMode === 'grid' ? (
            <GridContainer columns={3}>
              {blogs.map((blog) => (
                <BlogCard
                  key={blog.id}
                  blog={blog}
                  onEdit={() => navigate(`/blogs/${blog.id}/edit`)}
                  onDelete={(blog) => {
                    setSelectedBlog(blog);
                    setIsDeleteDialogOpen(true);
                  }}
                  onTogglePublish={handleTogglePublish}
                />
              ))}
            </GridContainer>
          ) : (
            <div className="space-y-4">
              {blogs.map((blog) => (
                <BlogListItem
                  key={blog.id}
                  blog={blog}
                  onEdit={() => navigate(`/blogs/${blog.id}/edit`)}
                  onDelete={(blog) => {
                    setSelectedBlog(blog);
                    setIsDeleteDialogOpen(true);
                  }}
                  onTogglePublish={handleTogglePublish}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {!isAuthor && (
          <TabsContent value="authors">
            <AuthorManagement />
          </TabsContent>
        )}
      </Tabs>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Blog"
        description="Are you sure you want to delete this blog post? This action cannot be undone."
      />
    </div>
  );
}
